import type { ProductCategoryId } from './constants'
import { courseTechniqueOf, findMenuTag, MENU_TAG_SECTIONS, type MenuTagValue } from './menu-tags'
import { MENU_TECHNIQUES, menuTechniqueNote } from './menu-techniques'
import type { MajorCategory, SubCategory, SubGroup } from './taxonomy'
import { TAXONOMY_I18N } from './taxonomy-i18n'

/**
 * 코스·테이스팅 축 (대표 확정 명세 2026-11-01 의 0장·6장. 원본은 recipecat-category-spec-20261101.md).
 * 만들기 첫 분류의 셋째 칸이다. 메뉴 축은 그대로이고 이 질문을 묻지 않는다.
 * - 대분류 = 코스 역할 10개 (명세 6장 순서). 이름은 4개 언어 모두 같은 id 의 메뉴 꼬리표 course 값(menu-tags.ts)을 읽는다.
 *   메뉴 꼬리표 화면과 코스 칸의 역할 이름이 같아야 해서 이름은 그 한 곳에만 둔다 (2026-11-01 점검 5번).
 *   셰프용 설명(카드 도움말이자 프롬프트 hint)은 명세 6장 그대로 여기 둔다. 인분·원가 기준은 프롬프트가 그 꼬리표 hint 에서 읽는다.
 * - 세부 = 그 역할의 스타일 + 기법. 대표 지시 "역할을 고른 뒤 가장 가까운 스타일·기법을 고르세요" (2026-11-01 점검 1·2번).
 *   스타일은 역할마다 다르다 (ROLE_STYLES). Main 은 대표가 "예시 (Main을 선택했을 때)" 로 적은 14칸 그대로다.
 *   기법 7개(MENU_TECHNIQUES)는 명세 1장 "메인 요리 하위 또는 공통 필터로" 에 따라 모든 역할에 남는다.
 *   스타일 목록이 기법 칸을 당겨 쓰면(Main 의 수비드·컨핏·발효) 그 기법은 스타일 묶음에만 한 번 나온다.
 * - 세부 id 는 `역할.항목` (예 main.sous-vide). 메뉴 축 세부와 글자가 같아도 모든 조회가 축을 같이 봐서 섞이지 않는다.
 *   id 는 한 번 내보내면 바꾸지 않는다 (저장된 제품·글이 들고 있다). 1.0.9 는 역할마다 같은 14칸(기법 7 + 국제 묶음 7)을 냈다.
 *   그 국제 묶음 칸은 역할 스타일이 같은 id 로 이어받거나(intermediate.pasta), 아니면 숨긴 칸으로 남는다 (dessert.pizza 등)
 * - 선택 꼬리표(단백질·스타일·식이 제한·서비스·상태)는 메뉴처럼 스타일·기법 다음 꼬리표 화면에서 고른다. 역할이 곧 대분류라
 *   그 화면의 '코스 역할' 섹션과 서비스 형태 '테이스팅 코스용' 은 빠진다 (menuTagSectionsFor). 기법 칸을 고르면 그 기법 꼬리표가
 *   미리 켜진다 (impliedMenuTags). 프롬프트는 코스 지시문 끝에 꼬리표 줄을 붙인다 (prompts/modes.ts courseAddendum)
 * - 1.0.8 까지의 앱은 이 축을 모르고, 1.0.9 는 역할마다 나눈 스타일을 모른다. 서버가 응답에서 낮춘다 (taxonomy-legacy.ts)
 * 역할 카드·세부 타일 사진은 apps/mobile/src/data/course-images.ts.
 * 사진 매핑 생성 스크립트가 4칸 들여쓴 `id: '…',` 줄을 역할 id 로 읽는다. 역할 표(ROLES) 밖에서는 그 모양을 쓰지 않는다
 */

interface Words {
  ko: string
  en: string
  ja: string
  zh: string
}

interface CourseRole {
  /** 메뉴 꼬리표 course 값의 id (4개 언어 이름과 인분 기준을 거기서 읽는다) */
  id: string
  /** 사진이 없을 때 카드에 그리는 글자 */
  emoji: string
  categoryId: ProductCategoryId
  /** 명세 6장의 셰프용 설명. 역할 카드 도움말로 보이고, ko 는 프롬프트 hint 로 들어간다 */
  desc: Words
}

const ROLES: CourseRole[] = [
  {
    id: 'amuse',
    emoji: '🥄',
    categoryId: 'meal',
    desc: {
      ko: '식사 전 한 입. 셰프의 인텐트 전달',
      en: 'One bite before the meal that shows the chef’s intent',
      ja: '食事前のひと口。シェフの意図を伝える',
      zh: '餐前一口，传达主厨的用意',
    },
  },
  {
    id: 'starter-cold',
    emoji: '🥗',
    categoryId: 'meal',
    desc: {
      ko: '냉채, 카르파치오, 세비체 등',
      en: 'Chilled plates such as carpaccio and ceviche',
      ja: '冷菜、カルパッチョ、セビーチェなど',
      zh: '冷菜、生肉薄片、柠汁腌鱼等',
    },
  },
  {
    id: 'starter-hot',
    emoji: '🍲',
    categoryId: 'meal',
    desc: { ko: '따뜻한 전채', en: 'A warm starter', ja: '温かい前菜', zh: '热的前菜' },
  },
  {
    id: 'intermediate',
    emoji: '🍝',
    categoryId: 'meal',
    desc: {
      ko: '메인 전 중간 코스',
      en: 'The in-between course before the main',
      ja: 'メインの前の中間の一皿',
      zh: '主菜前的中间一道',
    },
  },
  {
    id: 'fish',
    emoji: '🐟',
    categoryId: 'meal',
    desc: {
      ko: '생선 코스 (메인과 구분)',
      en: 'A fish course, separate from the main',
      ja: 'メインとは別の魚料理',
      zh: '鱼类一道（与主菜分开）',
    },
  },
  {
    id: 'main',
    emoji: '🥩',
    categoryId: 'meal',
    desc: { ko: '메인 코스', en: 'The main course', ja: 'メインコース', zh: '主菜一道' },
  },
  {
    id: 'pre-dessert',
    emoji: '🍧',
    categoryId: 'dessert',
    desc: {
      ko: '디저트 전 입가심 (소르베·그라니타·샤베트)',
      en: 'A palate cleanser before dessert (sorbet, granita, sherbet)',
      ja: 'デザート前の口直し（ソルベ・グラニテ・シャーベット）',
      zh: '甜点前的清口（雪葩、格兰尼塔、冰糕）',
    },
  },
  {
    id: 'dessert',
    emoji: '🍰',
    categoryId: 'dessert',
    desc: { ko: '본 디저트', en: 'The dessert itself', ja: 'メインのデザート', zh: '正式甜点' },
  },
  {
    id: 'mignardise',
    emoji: '🍬',
    categoryId: 'dessert',
    desc: {
      ko: '마지막 한 입 (페티푸르 등)',
      en: 'The last bite (petit fours and more)',
      ja: '最後のひと口（プティフールなど）',
      zh: '最后一口（花式小点等）',
    },
  },
  {
    id: 'cheese',
    emoji: '🧀',
    categoryId: 'meal',
    desc: {
      ko: '치즈 코스 (선택)',
      en: 'Cheese course (optional)',
      ja: 'チーズのコース（任意）',
      zh: '奶酪一道（可选）',
    },
  },
]

/** 스타일 한 칸: 이름 4개 언어 + 프롬프트 hint (그 접시를 내는 실무 기준: 인분, 선조리, 플레이팅, 원가 축) */
interface CourseStyle extends Words {
  id: string
  hint: string
}

const style = (
  id: string,
  ko: string,
  en: string,
  ja: string,
  zh: string,
  hint: string,
): CourseStyle => ({ id, ko, en, ja, zh, hint })

/** 기법 칸 (id·이름·hint 는 기법 꼬리표 그대로라 미리 켜는 꼬리표·프롬프트와 맞는다). 스타일 묶음으로 당겨 와도(Main 의 수비드·발효) 같다 */
function technique(id: string): CourseStyle {
  const tag = MENU_TECHNIQUES.find((t) => t.id === id)
  if (!tag) throw new Error(`technique ${id} is missing (course-taxonomy.ts)`)
  const { ko, en, ja, zh } = tag
  return { id, ko, en, ja, zh, hint: tag.hint }
}

/**
 * 역할마다의 스타일 (고르기 화면 첫 묶음, 이 순서로). 기법 묶음은 이 뒤에 붙는다.
 * Main 은 대표 원문(00_대표_지시_원문.md 의 transcript 59000 "예시 (Main을 선택했을 때)") 14칸 그대로다.
 *  - 수비드·저온조리, 발효·장 기반은 같은 이름의 기법 칸을 당겨 쓴다 (꼬리표·프롬프트 hint 가 하나로 맞는다)
 *  - 컨핏·숙성은 대표가 두 기법을 함께 적은 칸이라(첫 붙여넣기 "컨핏 / 숙성") 기법 하나가 아니라 스타일 칸이다.
 *    hint 가 둘 다 말하고, 미리 켜는 기법 꼬리표는 없다(어느 쪽인지는 사용자 설명이 정한다). id 는 1.0.9 의 컨핏 기법 칸
 *    main.confit 그대로라 Main 의 기법 묶음에는 컨핏 칸이 따로 없다. 숙성·에이징(건식·습식 숙성)은 기법 묶음에 남는다.
 *    고르기 화면에서는 셰프용 설명 줄(COURSE_STYLE_NOTES)과 함께 보인다
 *  - 파스타·리조또, 샐러드는 1.0.9 국제 묶음 칸(main.pasta, main.main-salad)이 뜻을 넓혀 이어받는다
 * 나머지 역할은 파인다이닝 관행 접시로 채웠다. 1.0.9 국제 묶음 칸과 같은 id 는 그 칸을 이어받는다 (intermediate.pasta 등)
 */
const ROLE_STYLES: Record<string, CourseStyle[]> = {
  amuse: [
    style(
      'canape',
      '카나페·타르틀렛',
      'Canapés & tartlets',
      'カナッペ・タルトレット',
      '开胃小饼·迷你挞',
      '바삭한 받침(크래커·타르트 셸·토스트) 위에 토핑을 올린 한 입. 받침은 미리 굽고 눅눅해지지 않게 제공 직전에 채운다. 1개 10~20g, 받침 파손률과 토핑 원가가 축.',
    ),
    style(
      'spoon',
      '스푼 한 입',
      'Spoon bite',
      'スプーンのひと口',
      '一勺小点',
      '서빙 스푼 하나에 담는 한 입(무스·타르타르·젤리·소스 한 방울). 구성 요소는 미리 만들고 제공 직전 스푼에 조립하는 시간이 축. 1개 8~15g, 스푼 수량과 세척이 운영 부담.',
    ),
    style(
      'shot',
      '샷·차가운 수프',
      'Shots & chilled soups',
      'ショット・冷製スープ',
      '小杯饮·冷汤',
      '작은 잔에 담는 차가운 수프(가스파초·비시수아즈·과채 샷). 대량으로 미리 만들어 냉장하고 1잔 30~50ml 를 붓기만 한다. 농도·간과 4도 안팎의 온도 유지가 qc, 잔 수량이 준비 단위.',
    ),
    style(
      'tuile',
      '튀일·칩·크래커',
      'Tuiles, chips & crackers',
      'チュイル・チップス・クラッカー',
      '瓦片脆·脆片·饼干',
      '얇게 펴 굽거나 말려 튀긴 바삭한 한 입(튀일·채소 칩·쌀 크래커)에 크림·토핑을 조금. 미리 대량으로 구워 밀폐 보관하고 습도 관리가 품질 축. 파손률이 원가 축.',
    ),
    style(
      'seafood-bite',
      '해산물 한 입',
      'Seafood bite',
      '魚介のひと口',
      '海鲜一口',
      '관자·굴·새우·성게·캐비어 같은 해산물 한 조각을 날것 또는 살짝 익혀 한 입에. 개당 해산물 원가가 크고 선도와 보관 온도가 qc. 제공 직전 손질·조립한다.',
    ),
    style(
      'vegetable-bite',
      '채소 한 입',
      'Vegetable bite',
      '野菜のひと口',
      '蔬菜一口',
      '제철 채소 한 조각을 굽거나 절이거나 퓌레·젤리로 바꾼 한 입. 원가가 낮은 대신 제철 수급과 색·식감 대비가 축. 1개 10~20g.',
    ),
  ],
  'starter-cold': [
    style(
      'naengchae',
      '냉채',
      'Naengchae (Korean cold plate)',
      '冷菜（ネンチェ）',
      '韩式冷菜',
      '해파리·해산물·채소를 데쳐 식힌 뒤 겨자·초간장 소스로 차게 무치는 전채. 재료는 미리 손질·냉장하고 제공 직전 소스에 버무린다. 1인 40~80g, 소스 선제조와 재료 선도가 축.',
    ),
    style(
      'carpaccio',
      '카르파치오',
      'Carpaccio',
      'カルパッチョ',
      '意式生薄片',
      '소고기·생선을 아주 얇게 썰어 접시에 펴고 올리브유·산미·치즈로 마감. 반쯤 얼려 썰거나 랩 사이에 두드려 펴는 손질 수율과 선도가 축. 1인 40~60g, 썬 뒤 냉장 보관 시간이 짧다.',
    ),
    style(
      'ceviche',
      '세비체',
      'Ceviche',
      'セビーチェ',
      '柠汁腌鱼',
      '생선·해산물을 라임·레몬즙(산)에 짧게 재워 익히는 차가운 전채. 재우는 시간(10~20분)과 산미 배합이 품질 축이라 주문 직전 버무린다. 1인 50~80g, 생식 위생(선도·보관 온도)을 steps 의 qc 에 적는다.',
    ),
    style(
      'tartare',
      '타르타르',
      'Tartare',
      'タルタル',
      '鞑靼生拌',
      '소고기·참치·연어를 잘게 다져 양념하고 링으로 모양을 잡는 차가운 전채 (육회와 같은 계열). 칼로 다지는 손질과 노른자·소스 배합이 축. 1인 50~80g, 다진 뒤 바로 내고 생식 위생을 qc 에 적는다.',
    ),
    style(
      'terrine',
      '테린·파테',
      'Terrine & pâté',
      'テリーヌ・パテ',
      '法式肉冻·肉酱',
      '틀에 채워 익히고 눌러 굳힌 뒤 썰어 내는 차가운 전채(고기·생선·채소 테린, 간 파테). 하루 이상 굳히는 선제조라 틀 하나에서 나오는 조각 수(수율)와 보관 일수가 원가 축. 1조각 40~70g.',
    ),
    style(
      'crudo',
      '회·크루도',
      'Sashimi & crudo',
      '刺身・クルード',
      '生鱼片·意式生鱼',
      '날생선·조개를 얇게 썰어 소금·기름·산미로 가볍게 마감하는 차가운 전채. 숙성·손질 수율과 선도가 원가 축, 제공 직전 썰고 접시를 차게 둔다. 1인 40~60g, 생식 위생을 qc 에 적는다.',
    ),
    style(
      'cold-salad',
      '차가운 샐러드',
      'Chilled salad',
      '冷たいサラダ',
      '冷沙拉',
      '잎채소·곡물·해산물을 차게 내는 전채 샐러드. 채소 손질 감량과 신선도, 드레싱 선제조가 축. 1인 40~80g, 주문 후 버무려 숨이 죽기 전에 낸다.',
    ),
  ],
  'starter-hot': [
    style(
      'seared',
      '구이·시어링',
      'Grilled & seared',
      'グリル・ソテー',
      '烤·香煎',
      '관자·문어·버섯·채소를 센 불에 굽거나 팬에 시어링해 따뜻하게 내는 전채. 주문 후 짧은 가열(1~3분)이라 화구 점유가 짧고, 재료 원가와 퓌레·소스 선제조가 축. 1인 50~100g.',
    ),
    style(
      'fry',
      '튀김·크로켓',
      'Fried bites & croquettes',
      '揚げ物・コロッケ',
      '炸物·可乐饼',
      '크로켓·튀김·프리토 같은 작은 튀김 전채. 반죽·빵가루 작업은 미리 하고 주문 후 튀겨 바로 낸다. 튀김기 유온과 몇 분 안에 나가는가(홀딩 시간)가 축. 1인 3~5조각.',
    ),
    style(
      'soup',
      '수프·벨루테',
      'Soups & veloutés',
      'スープ・ヴルーテ',
      '汤·丝绒浓汤',
      '채소·해산물 수프, 벨루테, 비스크를 작은 볼로. 육수·베이스는 대량 선제조, 주문 후 데워 크림·오일로 마감한다. 1인 80~120ml, 육수 원가와 농도 재현이 축.',
    ),
    style(
      'dumpling',
      '만두·딤섬',
      'Dumplings & dim sum',
      '餃子・点心',
      '饺子·点心',
      '빚어 두었다가 주문 후 찌거나 굽는 만두·딤섬. 피·소 작업(개당 빚는 시간)이 인건비 축이고 냉동 보관이 쉽다. 1인 2~4개, 찜기·팬 점유를 steps 에 적는다.',
    ),
    style(
      'gratin',
      '그라탱·오븐 요리',
      'Gratins & oven dishes',
      'グラタン・オーブン料理',
      '焗烤·烤箱料理',
      '작은 그릇(코코트)에 담아 오븐에 굽는 그라탱·에스카르고·구운 치즈. 미리 담아 두고 주문 후 굽는 시간(8~12분)이 오븐 점유, 그릇 수량이 준비 단위.',
    ),
    style(
      'pancake',
      '전·부침',
      'Jeon (Korean pan-fried)',
      'チヂミ・ジョン',
      '韩式煎饼',
      '한입 크기로 부친 전(육전·해물전·채소전). 반죽·재료는 미리 손질하고 주문 후 철판에 부쳐 바로 낸다. 철판 면적이 처리량 축. 1인 3~5조각, 기름 흡수와 바삭함 유지가 qc.',
    ),
  ],
  intermediate: [
    style(
      'pasta',
      '파스타',
      'Pasta',
      'パスタ',
      '意面',
      '메인보다 작은 접시 파스타(생면·필링 파스타 포함). 1인 건면 40~60g 또는 생면 60~80g. 면 반죽과 소스는 선제조, 주문 후 삶아 소스에 유화하는 화구 점유가 축.',
    ),
    style(
      'risotto-grain',
      '리조또·그레인',
      'Risotto & grains',
      'リゾット・グレイン',
      '烩饭·谷物',
      '리조또·오르조·보리·파로를 작은 접시로. 쌀·곡물을 반쯤 익혀 두는 선조리와 육수 원가, 주문 후 마무리(5~8분)가 축. 1인 생쌀 40~60g.',
    ),
    style(
      'consomme',
      '수프·콩소메',
      'Soups & consommé',
      'スープ・コンソメ',
      '汤·清汤',
      '맑게 거른 콩소메나 진한 수프를 작은 볼로. 육수 추출과 맑게 거르는 시간이 리드타임이고 뼈·향채 원가가 축. 1인 80~120ml, 주문 후 데워 고명만 올린다.',
    ),
    style(
      'egg',
      '달걀 요리',
      'Egg dishes',
      '卵料理',
      '蛋料理',
      '수란·온센 달걀·달걀찜(차완무시)·스크램블처럼 달걀이 중심인 작은 접시. 달걀 원가는 낮고 익힘 온도·시간(수조 63도 등)의 재현이 품질 축. 1인 달걀 1개 안팎, 소스·고명 원가가 더 크다.',
    ),
    style(
      'seafood',
      '해산물 작은 접시',
      'Small seafood plate',
      '魚介の小皿',
      '海鲜小盘',
      '조개·새우·관자·생선 한 조각을 굽거나 찌거나 소스에 데워 내는 작은 접시. 해산물 원가와 선도, 주문 후 짧은 가열이 축. 1인 40~70g.',
    ),
    style(
      'vegetable',
      '채소 요리',
      'Vegetable dish',
      '野菜料理',
      '蔬菜料理',
      '제철 채소 하나를 굽기·찜·퓌레·절임 같은 여러 조리로 한 접시에. 손질 감량과 제철 수급이 원가 축, 선조리 후 주문 후 데워 조립한다. 1인 60~100g.',
    ),
  ],
  fish: [
    style(
      'pan-seared',
      '팬 시어·구이',
      'Pan-seared & grilled',
      'ポワレ・グリル',
      '香煎·烤鱼',
      '생선 필레를 껍질부터 팬에 시어링하거나 구워 껍질을 바삭하게. 주문 후 조리(4~8분)와 심부 온도가 품질 축, 필레 손질 수율이 원가 축. 1인 60~100g.',
    ),
    style(
      'poached',
      '포칭·스팀',
      'Poached & steamed',
      'ポシェ・蒸し',
      '水煮·清蒸',
      '육수·버터·오일에 낮은 온도로 데치거나(포칭) 쪄서 촉촉하게. 60~80도의 온도와 시간 관리가 축이고 주문 후 조리라 화구·찜기 점유를 steps 에 적는다. 1인 60~100g.',
    ),
    style(
      'fry',
      '생선 튀김',
      'Fried fish',
      '魚のフライ',
      '炸鱼',
      '생선에 튀김옷·빵가루를 입혀 튀기거나 통째로 튀겨 낸다. 튀김옷 작업은 미리, 주문 후 튀김. 유온과 홀딩 시간이 축. 1인 60~100g, 가시 제거 수율이 원가 축.',
    ),
    style(
      'shellfish',
      '조개·갑각류',
      'Shellfish & crustaceans',
      '貝・甲殻類',
      '贝类·甲壳类',
      '랍스터·새우·게·관자·조개를 데치거나 굽거나 찐다. 살아 있는 상태 보관과 해감·손질 수율이 원가 축이고 시가 변동이 크다. 1인 살 기준 60~100g, 보관 온도와 생존 확인을 qc 에 적는다.',
    ),
    style(
      'crudo',
      '회·크루도',
      'Sashimi & crudo',
      '刺身・クルード',
      '生鱼片·意式生鱼',
      '생선 코스를 날것으로 낸다. 숙성·손질 수율과 선도가 원가 축, 제공 직전 썬다. 1인 60~80g, 생식 위생(보관 온도·기생충 관리)을 steps 의 qc 에 적는다.',
    ),
    style(
      'stew',
      '생선 수프·스튜',
      'Fish soups & stews',
      '魚のスープ・煮込み',
      '鱼汤·炖鱼',
      '부야베스·맑은 생선탕·해물 스튜처럼 국물째 내는 생선 코스. 생선 뼈 육수는 선제조, 주문 후 생선을 넣어 익히는 시간이 축. 1인 생선 60~80g + 국물 120~150ml.',
    ),
  ],
  main: [
    style(
      'grill',
      '구이·직화',
      'Grilled over fire',
      '焼き・直火',
      '烤·明火烤',
      '숯·그릴 직화로 굽는 메인. 열원과 부위·두께가 축이고 주문 후 굽기와 레스팅 시간을 steps 에 적는다. 단백질 1인 120~180g, 원육 시세가 원가를 정한다.',
    ),
    style(
      'wok',
      '볶음·웍',
      'Stir-fried (wok)',
      '炒め・中華鍋',
      '炒·镬炒',
      '센 불 웍에서 짧게 볶는 메인. 재료 손질과 양념장은 미리 맞춰 두고 주문 후 1~2인분씩 볶는다. 웍 1회 투입량이 처리 단위, 양념장 배합이 맛 축.',
    ),
    style(
      'braise',
      '조림·브레이즈',
      'Braised',
      '煮込み・ブレゼ',
      '炖·焖烧',
      '양념·와인·육수에 오래 졸이거나 브레이즈한 메인(갈비찜·숏립·오소부코). 전날 대량 선조리 후 소스째 보관, 주문 후 데워 소스를 졸여 마감. 조림 감량(수율)과 보관 일수가 원가 축.',
    ),
    style(
      'steam',
      '찜·스팀',
      'Steamed',
      '蒸し料理',
      '蒸',
      '찜기·압력으로 익혀 촉촉함을 살린 메인(생선찜·전복찜·닭찜). 찜기 단수와 조리 시간이 처리량 축, 선조리 후 다시 데울지를 정한다. 단백질 1인 120~180g.',
    ),
    style(
      'fry',
      '튀김·프라이',
      'Fried',
      '揚げ物・フライ',
      '炸',
      '튀김옷·빵가루를 입혀 튀기는 메인(커틀릿·돈가스·통닭 튀김). 튀김기 대수와 유온 회복이 피크 처리량 상한, 튀긴 뒤 몇 분 안에 내는가가 품질 축. 기름 교체 비용이 원가 항목.',
    ),
    style(
      'pancake',
      '전·부침',
      'Jeon (Korean pan-fried)',
      'チヂミ・ジョン',
      '韩式煎饼',
      '얇게 부쳐 겹겹이 담는 전 메인(육전·해물파전·생선전). 반죽 대비 건더기 비율이 원가와 맛을 좌우하고 철판 면적이 처리량. 주문 후 부쳐 바로 낸다.',
    ),
    style(
      'raw',
      '회·사시미·카르파치오',
      'Sashimi & carpaccio',
      '刺身・カルパッチョ',
      '生鱼片·意式生薄片',
      '날생선·날고기를 회·사시미·카르파치오로 내는 메인. 숙성·손질 수율과 선도가 원가 축이고 시가 변동이 크다. 1인 100~150g, 생식 위생(보관 온도·기생충 관리)을 steps 의 qc 에 적는다.',
    ),
    style(
      'roast',
      '스테이크·로스트·그릴',
      'Steak, roast & grill',
      'ステーキ・ロースト・グリル',
      '牛排·烤肉·炙烤',
      '팬 시어링·오븐 로스트·그릴로 주방에서 끝내는 스테이크·로스트(안심·양갈비·오리 가슴살). 심부 온도(굽기 정도)와 레스팅이 품질 축, 부위 원가와 트리밍 손실이 원가 축. 1인 120~180g.',
    ),
    style(
      'smoke',
      '훈연·바비큐',
      'Smoked & barbecue',
      '燻製・バーベキュー',
      '烟熏·烧烤',
      '저온 장시간 훈연·바비큐 메인(브리스킷·폭립·훈제 오리). 전일 준비가 전제라 하루 몇 인분을 미리 뽑는지와 감량(수율)이 원가 축. 주문 후에는 데워 썰고 소스로 마감.',
    ),
    technique('sous-vide'),
    style(
      'confit',
      '컨핏·숙성',
      'Confit & aging',
      'コンフィ・熟成',
      '油封·熟成',
      '지방에 저온으로 익혀 두는 컨핏(오리 다리·돼지 삼겹), 또는 건식·습식 숙성(드라이에이징)으로 맛을 올린 메인. 어느 쪽인지 사용자 설명을 따르고 internal.assumptions 에 적는다. 컨핏은 지방 사용량·재사용 횟수와 주문 후 껍질 마감, 숙성은 숙성 일수와 감량·트리밍 손실이 원가 축. 1인 120~180g.',
    ),
    technique('fermentation'),
    style(
      'pasta',
      '파스타·리조또',
      'Pasta & risotto',
      'パスタ・リゾット',
      '意面·烩饭',
      '메인 분량의 파스타·리조또. 1인 건면 80~100g 또는 생쌀 80g 안팎에 단백질을 곁들인다. 면·쌀 선조리와 소스 선제조, 주문 후 마무리 화구 점유가 축.',
    ),
    style(
      'main-salad',
      '샐러드',
      'Salad',
      'サラダ',
      '沙拉',
      '단백질·곡물을 올려 한 끼가 되는 메인 샐러드. 채소 손질 감량과 신선도, 단백질 선조리(구이·수비드)와 드레싱 선제조가 축. 주문 후 버무려 바로 낸다.',
    ),
  ],
  'pre-dessert': [
    style(
      'sorbet',
      '소르베·그라니타·샤베트',
      'Sorbet, granita & sherbet',
      'ソルベ・グラニテ・シャーベット',
      '雪葩·格兰尼塔·冰糕',
      '과일·허브로 만든 소르베·그라니타·샤베트를 작게 떠서. 대량 선제조 후 냉동 보관, 1인 30~50g. 당도(브릭스)와 산미가 입가심 축, 그릇은 미리 차게 둔다.',
    ),
    style(
      'jelly',
      '과일 젤리·콩소메',
      'Fruit jelly & consommé',
      'フルーツゼリー・コンソメ',
      '果冻·水果清汤',
      '과일즙을 젤라틴·한천으로 굳힌 젤리나 맑게 거른 과일 콩소메를 작은 잔·그릇에. 굳히는 시간이 리드타임이고 대량 선제조가 쉽다. 1인 40~60g, 과일 원가와 투명도가 품질 축.',
    ),
    style(
      'compote',
      '과일 콤포트',
      'Fruit compote',
      'フルーツコンポート',
      '糖煮水果',
      '제철 과일을 시럽·와인에 살짝 졸이거나 재워 차게 낸다. 과일 손질 감량과 시럽 재사용이 원가 축, 2~3일 냉장 선제조. 1인 40~60g.',
    ),
    style(
      'yogurt-cream',
      '요거트·크림 한 입',
      'Yogurt & cream bite',
      'ヨーグルト・クリームのひと口',
      '酸奶·奶油小点',
      '요거트·크림치즈·크렘 프레슈를 가볍게 휘핑해 과일·허브와 한 입으로. 산미로 입을 정리하는 자리라 단맛은 낮게, 선제조 후 제공 직전 담는다. 1인 30~50g.',
    ),
    style(
      'herb-flower',
      '허브·꽃 디저트',
      'Herb & flower dessert',
      'ハーブ・花のデザート',
      '香草·花卉甜点',
      '바질·민트·시소·식용 꽃·허브 오일로 향을 낸 가벼운 디저트(허브 그라니타·꽃 젤리). 허브·식용 꽃 수급과 시듦이 손실 축, 제공 직전 올린다. 1인 30~50g.',
    ),
  ],
  dessert: [
    style(
      'cake-tart',
      '케이크·타르트',
      'Cakes & tarts',
      'ケーキ・タルト',
      '蛋糕·挞',
      '조각 케이크·타르트를 접시 디저트로. 시트·셸은 대량 선제조, 주문 후 소스·아이스크림과 플레이팅한다. 한 판(홀)에서 나오는 조각 수가 원가 축.',
    ),
    style(
      'mousse-pudding',
      '무스·크림·푸딩',
      'Mousses, creams & puddings',
      'ムース・クリーム・プリン',
      '慕斯·奶油·布丁',
      '무스·판나코타·크렘 브륄레·푸딩처럼 굳히거나 휘핑한 디저트. 틀·그릇에 미리 굳혀(4시간 이상) 두고 주문 후 마감한다. 유제품 원가와 굳힘 재현이 축.',
    ),
    style(
      'ice-cream',
      '아이스크림·젤라또',
      'Ice cream & gelato',
      'アイスクリーム・ジェラート',
      '冰淇淋·意式冰淇淋',
      '직접 만든 아이스크림·젤라또를 크넬로 떠서 곁들임과 함께. 베이스 숙성과 기계 가동이 리드타임, 유지방·당도 배합이 식감 축. 1인 40~70g.',
    ),
    style(
      'chocolate',
      '초콜릿 디저트',
      'Chocolate dessert',
      'チョコレートデザート',
      '巧克力甜点',
      '초콜릿 무스·가나슈·앙트르메처럼 초콜릿이 중심인 디저트. 커버처 초콜릿 원가와 템퍼링·온도 관리가 축, 대부분 선제조 후 플레이팅한다.',
    ),
    style(
      'fruit',
      '과일 디저트',
      'Fruit dessert',
      'フルーツデザート',
      '水果甜点',
      '제철 과일을 굽기·졸이기·마리네이드로 살린 접시 디저트. 과일 수급과 손질 감량이 원가 축, 주문 후 조립한다.',
    ),
    style(
      'souffle-warm',
      '수플레·따뜻한 디저트',
      'Soufflés & warm desserts',
      'スフレ・温かいデザート',
      '舒芙蕾·热甜点',
      '수플레·퐁당 쇼콜라·구운 과일처럼 주문 후 오븐에서 굽는 따뜻한 디저트. 굽는 시간(8~15분)을 코스 흐름에 맞춰 미리 넣어야 하고, 오븐 점유와 실패율이 축.',
    ),
    style(
      'tteok-hangwa',
      '떡·한과 디저트',
      'Tteok & hangwa dessert',
      '餅・韓菓のデザート',
      '年糕·韩果甜点',
      '떡·약과·정과처럼 한국 전통 디저트를 코스 접시로. 떡은 굳기 전 당일 제조가 품질 축, 한과는 선제조 보관이 쉽다. 쌀·조청 원가와 손작업이 축.',
    ),
  ],
  mignardise: [
    style(
      'macaron',
      '마카롱',
      'Macarons',
      'マカロン',
      '马卡龙',
      '꼬끄를 굽고 필링을 채워 하루 숙성하는 한 입 과자. 대량 선제조 후 냉장·냉동 보관, 1개 10~15g. 꼬끄 실패율과 아몬드가루 원가가 축.',
    ),
    style(
      'bonbon',
      '봉봉·트러플 초콜릿',
      'Bonbons & chocolate truffles',
      'ボンボン・トリュフチョコレート',
      '夹心巧克力·松露巧克力',
      '템퍼링한 초콜릿 셸에 가나슈를 채운 봉봉, 가나슈를 굴린 트러플. 템퍼링·굳히는 시간이 리드타임, 커버처 원가와 보관 온도(15~18도)가 축. 1개 8~12g.',
    ),
    style(
      'pate-de-fruit',
      '파트 드 프뤼',
      'Pâte de fruit',
      'パート・ド・フリュイ',
      '法式水果软糖',
      '과일 퓌레를 펙틴·설탕과 졸여 굳힌 젤리 과자. 당도(브릭스)와 펙틴 배합이 재현 축, 굳힌 뒤 잘라 설탕을 묻혀 상온 보관한다. 1개 8~12g.',
    ),
    style(
      'madeleine',
      '마들렌·피낭시에',
      'Madeleines & financiers',
      'マドレーヌ・フィナンシェ',
      '玛德琳·费南雪',
      '반죽을 미리 휴지시켜 두고 서비스 직전에 구워 따뜻하게 내는 작은 구움과자. 틀 수와 오븐 시간이 처리량, 버터·아몬드가루 원가가 축. 1개 10~15g.',
    ),
    style(
      'nougat-caramel',
      '누가·캐러멜',
      'Nougat & caramels',
      'ヌガー・キャラメル',
      '牛轧糖·焦糖',
      '설탕·꿀을 정확한 온도로 끓여 견과·버터와 굳힌 누가·캐러멜. 끓이는 온도 관리가 재현 축, 굳혀 잘라 하나씩 싸서 보관한다. 1개 8~12g.',
    ),
    style(
      'jeonggwa-hangwa',
      '정과·한과',
      'Jeonggwa (candied roots) & hangwa',
      'チョングァ（韓国の砂糖漬け）・韓菓',
      '韩式蜜饯（正果）·韩果',
      '연근·생강·도라지를 조청에 졸인 정과나 약과·다식 같은 한과를 한 입 크기로. 졸이고 말리는 선제조가 길고 상온 보관이 쉽다. 조청·꿀 원가와 손작업이 축.',
    ),
  ],
  cheese: [
    style(
      'plate',
      '치즈 플레이트',
      'Cheese plate',
      'チーズプレート',
      '奶酪拼碟',
      '숙성 정도·우유 종류가 다른 치즈 2~4종을 곁들임과 한 접시에. 조리보다 치즈 매입·보관(온도·습도)과 1인 중량(40~60g)이 원가 축. 제공 30분 전 실온에 꺼내 둔다.',
    ),
    style(
      'hard-aged',
      '숙성 하드 치즈',
      'Aged hard cheese',
      '熟成ハードチーズ',
      '熟成硬质奶酪',
      '콩테·파르미지아노·고다처럼 오래 숙성한 단단한 치즈를 얇게 깎거나 조각으로. 숙성 기간이 단가를 정하고, 자른 면이 마르지 않게 싸서 보관하는 것이 손실 축. 1인 30~50g.',
    ),
    style(
      'fresh-soft',
      '프레시·연성 치즈',
      'Fresh & soft cheese',
      'フレッシュ・ソフトチーズ',
      '新鲜·软质奶酪',
      '부라타·리코타·브리·카망베르처럼 숙성이 짧거나 부드러운 치즈. 유통기한이 짧아 발주 단위와 폐기율이 원가 축, 차게 보관했다가 제공 전에 온도를 맞춘다. 1인 40~60g.',
    ),
    style(
      'blue',
      '블루치즈',
      'Blue cheese',
      'ブルーチーズ',
      '蓝纹奶酪',
      '고르곤졸라·로크포르·스틸턴 같은 푸른곰팡이 치즈를 꿀·배·견과와. 향이 강해 소량(1인 20~40g)으로 내고, 다른 치즈와 따로 싸서 보관한다.',
    ),
    style(
      'pairing',
      '치즈 곁들임 (꿀·잼·견과)',
      'Cheese pairings (honey, jam, nuts)',
      'チーズのお供（蜂蜜・ジャム・ナッツ）',
      '奶酪配料（蜂蜜·果酱·坚果）',
      '치즈에 곁들이는 꿀·잼·처트니·견과·크래커를 직접 만들어 내는 구성. 곁들임은 대량 선제조·상온 보관이 쉬워 원가를 낮추고 치즈는 소량. 1인 치즈 30~40g + 곁들임 20~30g.',
    ),
    style(
      'warm',
      '따뜻한 치즈 요리',
      'Warm cheese dish',
      '温かいチーズ料理',
      '热奶酪料理',
      '구운 카망베르·라클렛·치즈 수플레처럼 치즈를 데워 녹여 내는 요리. 주문 후 오븐·토치 가열(5~10분)이 축, 녹는 치즈 종류와 1인 중량(50~80g)이 원가를 정한다.',
    ),
  ],
}

/** 1.0.9 국제 묶음 한 칸: 이름을 읽을 메뉴 분류 자리(국제·서양 메인의 묶음, 또는 메뉴 세부) + 코스 접시용 hint */
interface ShippedDishForm {
  id: string
  from: { group: string } | { majorId: string; subId: string }
  hint: string
}

const shipped = (id: string, from: ShippedDishForm['from'], hint: string): ShippedDishForm => ({
  id,
  from,
  hint,
})

/**
 * 1.0.9 가 모든 역할에 낸 국제 묶음 7칸 (명세 2장 국제·서양 메인 묶음 5 + 타파스 + 초밥). 이름은 메뉴 분류에서 읽는다.
 * 역할 스타일(ROLE_STYLES)에 같은 id 가 있으면 그 칸이 이어받고, 없으면 hiddenInPicker 로 남는다 (저장된 제품·글의 라벨·프롬프트용).
 * hint 는 코스 접시용이다 (메뉴 세부 hint 에는 다른 메뉴 분류로 안내하는 문장이 섞여 있다)
 */
const SHIPPED_DISH_FORMS: ShippedDishForm[] = [
  shipped(
    'pasta',
    { group: 'pasta' },
    '드라이·프레시·필링(라비올리·토르텔리니)·오븐 베이크 파스타. 반죽·건조 여부와 삶는 시간, 소스 유화가 축. 주문 후 삶아 마무리하는 화구 점유를 steps 에 적는다.',
  ),
  shipped(
    'risotto-grain',
    { group: 'risotto-grain' },
    '리조또·오르조·파로·퀴노아 그레인 볼. 곡물을 반쯤 익혀 두는 선조리 구조와 육수 원가, 주문 후 마무리 시간이 축.',
  ),
  shipped(
    'pizza',
    { group: 'pizza' },
    '나폴리·로만·알 타글리오 피자, 포카치아, 플랫브레드. 도우 발효 시간과 오븐 온도·굽는 시간, 토핑 원가가 축.',
  ),
  shipped(
    'burger-sandwich',
    { group: 'burger-sandwich' },
    '수제 버거, 클럽·오픈·바게트 샌드위치. 빵 수급과 패티·속재료 선조리, 주문 후 조립 시간이 축.',
  ),
  shipped(
    'main-salad',
    { group: 'main-salad' },
    '프로틴·그레인·웜 샐러드. 단백질·곡물 구성과 드레싱, 채소 손질 감량과 신선도 관리가 축.',
  ),
  shipped(
    'tapas',
    { majorId: 'side', subId: 'side.tapas' },
    '한두 입 크기의 작은 접시. 접시당 단가와 선조리 비율, 여러 접시를 빠르게 내는 제공 속도가 축.',
  ),
  shipped(
    'sushi',
    { majorId: 'main', subId: 'main.sushi' },
    '회와 다른 칸이다. 샤리(초밥밥) 배합·온도와 생선 숙성·손질, 쥐는 기술이 축. 생식 위생(보관 온도·기생충 관리)을 steps 의 qc 에 적는다.',
  ),
]

function requireTag(axis: 'course', id: string): MenuTagValue {
  const tag = findMenuTag(axis, id)
  if (!tag) throw new Error(`menu tag ${axis}:${id} is missing (course-taxonomy.ts)`)
  return tag
}

export interface CourseTaxonomy {
  /** 코스 역할 10개. TAXONOMY.course */
  majors: MajorCategory[]
  /** 일본어·중국어 이름. 키 형식은 TAXONOMY_I18N 과 같다 ("course:역할", "course:역할:역할.항목") */
  i18n: Record<string, { ja: string; zh: string }>
}

/** 역할 세부 한 칸과 놓이는 묶음. hidden 이면 1.0.9 에 나간 뒤 이 역할에서 물러난 칸 */
interface RoleSub {
  style: CourseStyle
  group: 'style' | 'technique'
  hidden: boolean
}

/**
 * 코스 축 표를 만든다. 물러난 국제 묶음 칸의 이름을 메뉴 분류에서 읽으므로 taxonomy.ts 가 메뉴 대분류를 만든 뒤에 부른다
 * (이 파일이 taxonomy.ts 를 불러오면 서로 기다리게 된다).
 * 역할마다 세부 순서: 스타일(ROLE_STYLES) → 스타일이 당겨 가지 않은 기법 → 물러난 1.0.9 국제 묶음 칸(숨김)
 */
export function buildCourseTaxonomy(menuMajors: readonly MajorCategory[]): CourseTaxonomy {
  const intl = menuMajors.find((m) => m.id === 'intl')
  if (!intl) throw new Error('menu major intl is missing (course-taxonomy.ts)')
  const formName = (from: ShippedDishForm['from']): Words => {
    if ('group' in from) {
      const group = intl.groups?.find((g) => g.id === from.group)
      if (!group) throw new Error(`intl group ${from.group} is missing (course-taxonomy.ts)`)
      return { ko: group.ko, en: group.en, ja: group.ja, zh: group.zh }
    }
    const sub = menuMajors.find((m) => m.id === from.majorId)?.subs.find((x) => x.id === from.subId)
    const i18n = TAXONOMY_I18N[`menu:${from.majorId}:${from.subId}`]
    if (!sub || !i18n) throw new Error(`menu sub ${from.subId} is missing (course-taxonomy.ts)`)
    return { ko: sub.ko, en: sub.en, ja: i18n.ja, zh: i18n.zh }
  }
  const shippedForms: CourseStyle[] = SHIPPED_DISH_FORMS.map(({ id, from, hint }) => ({
    id,
    ...formName(from),
    hint,
  }))
  const techniques = MENU_TECHNIQUES.map((t) => technique(t.id))
  const techniqueSection = MENU_TAG_SECTIONS.find((section) => section.id === 'technique')!
  const groups: SubGroup[] = [
    { id: 'style', ko: '스타일', en: 'Style', ja: 'スタイル', zh: '风格' },
    {
      id: 'technique',
      ko: techniqueSection.ko,
      en: techniqueSection.en,
      ja: techniqueSection.ja,
      zh: techniqueSection.zh,
    },
  ]

  const roleSubs = (roleId: string): RoleSub[] => {
    const styles = ROLE_STYLES[roleId]
    if (!styles) throw new Error(`course role ${roleId} has no styles (course-taxonomy.ts)`)
    const own = new Set(styles.map((s) => s.id))
    return [
      ...styles.map((s) => ({ style: s, group: 'style' as const, hidden: false })),
      ...techniques
        .filter((s) => !own.has(s.id))
        .map((s) => ({ style: s, group: 'technique' as const, hidden: false })),
      ...shippedForms
        .filter((s) => !own.has(s.id))
        .map((s) => ({ style: s, group: 'style' as const, hidden: true })),
    ]
  }

  const majors = ROLES.map((role): MajorCategory => {
    const tag = requireTag('course', role.id)
    return {
      id: role.id,
      emoji: role.emoji,
      ko: tag.ko,
      en: tag.en,
      categoryId: role.categoryId,
      hint: role.desc.ko,
      groups,
      subs: roleSubs(role.id).map(({ style: s, group, hidden }): SubCategory => ({
        id: `${role.id}.${s.id}`,
        ko: s.ko,
        en: s.en,
        hint: s.hint,
        group,
        ...(hidden ? { hiddenInPicker: true } : {}),
      })),
    }
  })
  const i18n = Object.fromEntries(
    ROLES.flatMap((role) => {
      const tag = requireTag('course', role.id)
      return [
        [`course:${role.id}`, { ja: tag.ja, zh: tag.zh }],
        ...roleSubs(role.id).map(({ style: s }) => [
          `course:${role.id}:${role.id}.${s.id}`,
          { ja: s.ja, zh: s.zh },
        ]),
      ]
    }),
  ) as CourseTaxonomy['i18n']
  return { majors, i18n }
}

/**
 * 기법이 아닌데 셰프용 설명 줄이 필요한 스타일 칸. Main 의 컨핏·숙성은 기법 묶음의 숙성·에이징과 어느 쪽을 고를지
 * 화면에서 알 수 있어야 한다 (2026-11-01 점검 10번)
 */
const COURSE_STYLE_NOTES: Record<string, Words> = {
  'main.confit': {
    ko: '지방에 천천히 익혀 두는 컨핏, 또는 숙성 메인. 숙성만이면 아래 숙성·에이징',
    en: 'Slow-cooked confit kept in fat, or an aged main. For aging alone, pick Aging below',
    ja: '脂でじっくり火を入れるコンフィ、または熟成メイン。熟成だけなら下の熟成・エイジング',
    zh: '在油脂中慢煮保存的油封，或熟成主菜。只做熟成请选下方的熟成',
  },
}

/**
 * 고르기 화면에서 세부 이름 아래 보이는 셰프용 설명 줄. 기법 칸은 기법 설명(menu-techniques.ts, 꼬리표 화면의 기법 줄과 같은 말),
 * 그 밖에는 COURSE_STYLE_NOTES. 설명이 없는 칸은 '' (사진 타일로 그린다)
 */
export function courseCellNote(subId: string, language: string): string {
  const technique = courseTechniqueOf(subId)
  if (technique) return menuTechniqueNote(technique, language) ?? ''
  const note = COURSE_STYLE_NOTES[subId]
  if (!note) return ''
  return language === 'ko' || language === 'ja' || language === 'zh' ? note[language] : note.en
}

/** 역할 카드·세부 화면 머리의 도움말 (명세 6장 셰프용 설명). 모르는 역할은 '' */
export function courseRoleDescription(roleId: string, language: string): string {
  const desc = ROLES.find((role) => role.id === roleId)?.desc
  if (!desc) return ''
  return language === 'ko' || language === 'ja' || language === 'zh' ? desc[language] : desc.en
}
